import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ConfigInterface, Order } from '@vendasta/sales-orders';
import { OrderFormOptionsInterface } from '@vendasta/store';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { AccountGroup } from '@galaxy/account-group';
import { PaymentService, RetailProvider, StripeService } from '@galaxy/billing';
import { BrandingV2Service } from '@galaxy/partner';
import { User } from '@vendasta/iam';
import { ORDER_FORM_FILE_UPLOAD_URL } from '../../utils';

interface previewData {
  shouldCollectPayment: boolean;
  order: Order;
  orderConfig: ConfigInterface;
  business: AccountGroup;
  users: User[];
}

@Component({
  templateUrl: './preview-public-order-dialog.component.html',
  styleUrls: ['./preview-public-order-dialog.component.scss'],
  standalone: false,
})
export class PreviewPublicOrderDialogComponent {
  protected order: Order;
  protected orderConfig: ConfigInterface;
  protected userOptions: User[];
  protected retailProvider$: Observable<RetailProvider>;
  protected fileUploadUrl = ORDER_FORM_FILE_UPLOAD_URL + '/';
  public title: string;
  public subTitle: string;
  protected stripeKey: string;
  protected partnerName$: Observable<string>;
  protected orderFormOptions: OrderFormOptionsInterface = {
    bypassRequiredQuestions: true,
    readOnly: true,
    showOfficeUseQuestions: true,
  };
  protected shouldCollectPayment: boolean;
  constructor(
    public dialogRef: MatDialogRef<PreviewPublicOrderDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: previewData,
    private paymentService: PaymentService,
    private stripeService: StripeService,
    private brandingService: BrandingV2Service,
  ) {
    this.shouldCollectPayment = data.shouldCollectPayment;
    this.stripeKey = this.stripeService.getConnectPublicKey();
    this.order = data.order;
    this.orderConfig = data.orderConfig;
    this.userOptions = data.users;
    const businessData = data.business;
    this.partnerName$ = this.brandingService.getBranding(this.order.partnerId).pipe(
      map((branding) => branding.name),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.title = businessData?.napData?.companyName || '-';
    this.subTitle = this.formatPageSubtitle(businessData) || '-';

    this.retailProvider$ = this.paymentService.getRetailProvider(this.order?.partnerId).pipe(
      catchError(() => of(null)),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  private formatPageSubtitle(business: AccountGroup): string {
    if (business?.napData) {
      const address = [business.napData.address, business.napData.city, business.napData.state, business.napData.zip]
        .filter((item) => item)
        .join(', ');

      const primaryNumber: string | undefined = (business.napData.workNumber || [])[0];
      const phoneNumberItem = primaryNumber ? `phone number: ${primaryNumber}` : '';

      let customerIdItem = '';
      if (business.externalIdentifiers && business.externalIdentifiers?.customerIdentifier) {
        customerIdItem = `customer identifier: ${business.externalIdentifiers?.customerIdentifier}`;
      }
      return [address, phoneNumberItem, customerIdItem].filter((i) => i).join(' | ');
    }

    return '';
  }
}
